import React, { useEffect, useRef, useState } from 'react';
import classes from './BottomTabs.module.css';

// Computed once, not per-render — same reasoning as useDragItem.js's
// isTouchCapable / NeedScreen.js's own prefersReducedMotion.
const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Active tab icon/label use --brand-nav, not --brand-primary directly — an
// accessibility-adjusted variant of the festival's brand colour computed in
// Root.js (darkened just enough to keep >=3:1 contrast against this nav's
// white background), so a light/bright brand colour doesn't make the
// active tab unreadable. See Root.js's ensureAccessibleNavColour.
const HomeIcon = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M20.0402 6.82165L14.2802 2.79165C12.7102 1.69165 10.3002 1.75165 8.79023 2.92165L3.78023 6.83165C2.78023 7.61165 1.99023 9.21165 1.99023 10.4716V17.3716C1.99023 19.9216 4.06023 22.0016 6.61023 22.0016H17.3902C19.9402 22.0016 22.0102 19.9316 22.0102 17.3816V10.6016C22.0102 9.25165 21.1402 7.59165 20.0402 6.82165Z" fill={active ? "var(--brand-nav)" : "#abacac"}/>
  </svg>
);

const MyGearIcon = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M13 5H21" stroke={active ? "var(--brand-nav)" : "#abacac"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13 9H18" stroke={active ? "var(--brand-nav)" : "#abacac"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13 15H21" stroke={active ? "var(--brand-nav)" : "#abacac"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13 19H18" stroke={active ? "var(--brand-nav)" : "#abacac"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 5C3 4.73478 3.10536 4.48043 3.29289 4.29289C3.48043 4.10536 3.73478 4 4 4H8C8.26522 4 8.51957 4.10536 8.70711 4.29289C8.89464 4.48043 9 4.73478 9 5V9C9 9.26522 8.89464 9.51957 8.70711 9.70711C8.51957 9.89464 8.26522 10 8 10H4C3.73478 10 3.48043 9.89464 3.29289 9.70711C3.10536 9.51957 3 9.26522 3 9V5Z" stroke={active ? "var(--brand-nav)" : "#abacac"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 15C3 14.7348 3.10536 14.4804 3.29289 14.2929C3.48043 14.1054 3.73478 14 4 14H8C8.26522 14 8.51957 14.1054 8.70711 14.2929C8.89464 14.4804 9 14.7348 9 15V19C9 19.2652 8.89464 19.5196 8.70711 19.7071C8.51957 19.8946 8.26522 20 8 20H4C3.73478 20 3.48043 19.8946 3.29289 19.7071C3.10536 19.5196 3 19.2652 3 19V15Z" stroke={active ? "var(--brand-nav)" : "#abacac"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const NeedIcon = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M17.7402 3.53125C17.1107 3.33094 16.4522 3.24245 15.7939 3.26562L9.23728 9.82324C9.19046 9.87008 9.16408 9.93377 9.16404 10C9.16404 10.0662 9.19053 10.1299 9.23728 10.1768L9.78025 10.7197C10.1774 11.1167 10.8226 11.1168 11.2197 10.7197L12.2197 9.71972L12.3603 9.58496C13.0816 8.93183 14.0223 8.5684 15 8.56836L15.1953 8.57324C16.1669 8.62156 17.0891 9.02866 17.7803 9.71972L20.5605 12.5C20.8748 12.0918 21.1353 11.6427 21.3301 11.1592C21.6256 10.4253 21.7666 9.6375 21.7441 8.84668C21.7215 8.05604 21.536 7.27819 21.1992 6.5625C20.9044 5.93606 20.4993 5.36809 20.0039 4.88672L19.7861 4.68554C19.191 4.16418 18.4941 3.77121 17.7402 3.53125Z" fill={active ? "var(--brand-nav)" : "#abacac"}/>
    <path d="M8.59763 3.2705C7.90696 3.19683 7.20867 3.24922 6.53806 3.42383L6.25291 3.50586C5.49632 3.74522 4.7973 4.13947 4.20017 4.66211C3.67777 5.11945 3.24251 5.66743 2.91599 6.27832L2.7822 6.54394C2.44487 7.26232 2.2602 8.04356 2.23923 8.83691C2.21834 9.6303 2.36209 10.4201 2.66111 11.1553C2.95929 11.8882 3.40633 12.552 3.97263 13.1045L11.4726 20.5322C11.7649 20.8217 12.236 20.8217 12.5283 20.5322L14.5088 18.5693L11.9697 16.0303L11.9179 15.9736C11.6777 15.6791 11.6951 15.2443 11.9697 14.9697C12.2443 14.6952 12.6791 14.6778 12.9736 14.918L13.0303 14.9697L15.5742 17.5137L17.0195 16.0801L14.4697 13.5303L14.4179 13.4736C14.1777 13.1791 14.1951 12.7443 14.4697 12.4697C14.7443 12.1952 15.1791 12.1778 15.4736 12.418L15.5303 12.4697L18.0859 15.0254L19.5322 13.5928L16.7197 10.7803C16.3207 10.3813 15.7969 10.1354 15.2402 10.0801L15 10.0684C14.4357 10.0684 13.8917 10.2646 13.459 10.6191L13.2803 10.7803L12.2803 11.7803C11.2974 12.7631 9.7026 12.7631 8.7197 11.7803L8.17673 11.2373C7.84877 10.9091 7.66404 10.4639 7.66404 10C7.66408 9.53603 7.84871 9.09083 8.17673 8.76269L12.0029 4.87402C11.7252 4.60303 11.4211 4.35958 11.0937 4.14941L10.8388 3.99511C10.1498 3.60162 9.38661 3.35475 8.59763 3.2705Z" fill={active ? "var(--brand-nav)" : "#abacac"}/>
  </svg>
);

const PackIcon = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M20.2083 7.82141L12.5083 12.2814C12.1983 12.4614 11.8083 12.4614 11.4883 12.2814L3.78826 7.82141C3.23826 7.50141 3.09826 6.75141 3.51826 6.28141C3.80826 5.95141 4.13826 5.68141 4.48826 5.49141L9.90826 2.49141C11.0683 1.84141 12.9483 1.84141 14.1083 2.49141L19.5283 5.49141C19.8783 5.68141 20.2083 5.96141 20.4983 6.28141C20.8983 6.75141 20.7583 7.50141 20.2083 7.82141Z" fill={active ? "var(--brand-nav)" : "#abacac"}/>
    <path d="M11.4305 14.1389V20.9589C11.4305 21.7189 10.6605 22.2189 9.98047 21.8889C7.92047 20.8789 4.45047 18.9889 4.45047 18.9889C3.23047 18.2989 2.23047 16.5589 2.23047 15.1289V9.9689C2.23047 9.1789 3.06047 8.6789 3.74047 9.0689L10.9305 13.2389C11.2305 13.4289 11.4305 13.7689 11.4305 14.1389Z" fill={active ? "var(--brand-nav)" : "#abacac"}/>
    <path d="M12.5703 14.1389V20.9589C12.5703 21.7189 13.3403 22.2189 14.0203 21.8889C16.0803 20.8789 19.5503 18.9889 19.5503 18.9889C20.7703 18.2989 21.7703 16.5589 21.7703 15.1289V9.9689C21.7703 9.1789 20.9403 8.6789 20.2603 9.0689L13.0703 13.2389C12.7703 13.4289 12.5703 13.7689 12.5703 14.1389Z" fill={active ? "var(--brand-nav)" : "#abacac"}/>
  </svg>
);

const TABS = [
  { id: 'home',    label: 'Home',    Icon: HomeIcon },
  { id: 'list',    label: 'My Gear', Icon: MyGearIcon },
  { id: 'need',    label: 'Need',    Icon: NeedIcon },
  { id: 'pack',    label: 'Pack',    Icon: PackIcon },
];

const BottomTabs = ({ currentScreen, setCurrentScreen, group, festivalInfo, packBadgeCount = 0, packBumpTrigger = 0 }) => {
  // Urgency badge on My Gear — matches the same fixed-amber "days to go
  // with unsorted items" concept as the Home screen's progress card, kept
  // independent of any brand colour on purpose (see Home.js for the mirror
  // of this calculation).
  //
  // group.festival is a snapshot copied onto the group once, when the event
  // was added — it never updates again, so it goes stale the moment an
  // admin edits that festival's dates afterwards. festivalInfo is the live
  // festivals/{id} doc (see Root.js's onSnapshot listener) — prefer it,
  // falling back to group.festival only for a custom event (no catalog
  // festival, so no live doc to listen to).
  const currentFestival = festivalInfo || group?.festival;
  const startdate = currentFestival?.startdate;
  const daysUntilFestival = startdate
    ? Math.ceil((new Date(startdate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const festivalEnddate = currentFestival?.enddate;
  const hasEnded = festivalEnddate ? new Date(festivalEnddate) - new Date() <= 0 : false;
  const unsortedCount = (group?.items || []).filter(
    i => (i.ownQuantity || 0) + (i.needQuantity || 0) === 0
  ).length;
  const isUrgent = daysUntilFestival !== null && daysUntilFestival <= 28 && unsortedCount > 0 && !hasEnded;
  const badgeText = unsortedCount > 99 ? '99+' : String(unsortedCount);
  const packBadgeText = packBadgeCount > 99 ? '99+' : String(packBadgeCount);

  // Brief scale bump on Pack's icon each time an item is sorted there from
  // Need (see NeedScreen.js's handleMoveToPack / Root.js's
  // handleSortedToPack) — packBumpTrigger is a bare counter; only *changes*
  // to it matter, not its value, so a fresh sort always replays the bump
  // even if a previous one hasn't finished.
  const [packBumping, setPackBumping] = useState(false);
  const prevBumpTriggerRef = useRef(packBumpTrigger);
  useEffect(() => {
    if (packBumpTrigger === prevBumpTriggerRef.current) return undefined;
    prevBumpTriggerRef.current = packBumpTrigger;
    if (prefersReducedMotion) return undefined;
    setPackBumping(true);
    const t = setTimeout(() => setPackBumping(false), 400);
    return () => clearTimeout(t);
  }, [packBumpTrigger]);

  return (
    <nav className={classes.bottomNav}>
      {TABS.map(({ id, label, Icon }) => {
        const active = currentScreen === id;
        return (
          <button
            key={id}
            className={`${classes.tab} ${active ? classes.tabActive : ''}`}
            onClick={() => setCurrentScreen(id)}
            aria-label={label}
          >
            <span className={`${classes.tabIcon} ${id === 'pack' && packBumping ? classes.tabIconBump : ''}`}>
              <Icon active={active} />
              {id === 'list' && isUrgent && (
                <span className={classes.tabBadge}>{badgeText}</span>
              )}
              {id === 'pack' && packBadgeCount > 0 && (
                <span className={`${classes.tabBadge} ${classes.tabBadgePack}`}>{packBadgeText}</span>
              )}
            </span>
            <span className={classes.tabLabel}>{label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomTabs;
